// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { useClientGetByIdMutation } from '../Client.api'
import { Spinner } from '../../../common/component/Spinner'
import { IClientDto, IClientNestedDto } from '../Client.model'
import { IconLabelContent } from '../../../common/component/IconLabelContent'
import { useAppSelector } from '../../store/Store'
import { selectClient } from '../Client.slice'

interface Props {
  client: IClientNestedDto
}

export const ClientEmbedered: FC<Props> = ({client}: Props) => {
  const intl = useIntl()
  const storeData = useAppSelector(selectClient)
  const didFetchData = useRef(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<IClientDto | undefined>()
  const [getById] = useClientGetByIdMutation()

  useEffect(() => {
    const fetchData = async (id: string) => {
      try {
        if (!didFetchData.current) {
          const response: any = await getById({id})
          if (!response || response.error) {
            setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            setData(response.data)
          }
        }
      } catch (error) {
        setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsLoading(false)
      }
    }
    if (storeData && storeData.id === client.id) {
      setData(storeData)
      setIsLoading(false)
    } else {
      fetchData(client.id)
    }
  }, [client, getById, intl, storeData])

  const buildDescriptionLine = (data: IClientDto) => {
    let description = `${data.sector}`
    if (data.address) {
      description += description ? ` | ${data.address}` : `${data.address}`
    }

    return description
  }

  return (
    <>
      <CustomInputRenderer
        label={intl.formatMessage({id: 'UTIL.CLIENT'})}
        object={
          !data && isLoading ? (
            <Spinner />
          ) : data ? (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='fs-7 cursor-pointer'>
                      <Link className='' to={`/clients/${data.id}`} target='_blank'>
                        <span className='fw-bolder mb-2'>{data.displayName}</span>
                        <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                        <span className='text-primary mb-2'>{data.sequence}</span>
                      </Link>
                    </div>

                    <div className='d-flex justify-content-start'>
                      <div className='d-flex align-items-center fs-8'>
                        <span className='text-gray-600'>{buildDescriptionLine(data)}</span>
                      </div>
                    </div>

                    <div className='d-flex justify-content-start align-items-center'>
                      <IconLabelContent
                        title={`${intl
                          .formatMessage({id: 'TITLE.CONTRACT.TOTAL'})
                          .replace(/%s/g, `${data.totalContract > 0 ? data.totalContract : 0}`)}`}
                        labelValue={`${data.totalContract || 0}`}
                        active={data.totalContract > 0}
                        icon='bi bi-vector-pen'
                      />

                      <IconLabelContent
                        title={`${intl
                          .formatMessage({id: 'TITLE.ASSET.TOTAL'})
                          .replace(/%s/g, `${data.totalAsset > 0 ? data.totalAsset : 0}`)}`}
                        labelValue={`${data.totalAsset || 0}`}
                        active={data.totalAsset > 0}
                        icon='bi bi-cpu'
                      />

                      <IconLabelContent
                        title={`${intl
                          .formatMessage({id: 'TITLE.REPORT.TOTAL'})
                          .replace(/%s/g, `${data.totalReport > 0 ? data.totalReport : 0}`)}`}
                        labelValue={`${data.totalReport || 0}`}
                        active={data.totalReport > 0}
                        icon='bi bi-speedometer'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='fs-7'>
                      <span className='fw-bolder mb-2'>{client.displayName}</span>
                      <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
                      <span className='text-primary mb-2'>{client.sequence}</span>
                    </div>
                    {error && (
                      <span className='text-danger '>
                        <div className='d-flex flex-row justify-content-start align-items-center'>
                          <span className='fs-8 me-1'>
                            <i className='text-danger me-1 bi bi-exclamation-octagon' />
                            {error}
                          </span>
                        </div>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ></CustomInputRenderer>
    </>
  )
}
